import qr from "qrcode-generator";

export function qrCodeVersionForUrl(url: string): number {
  // Assuming the "M" error correction level used by our QR code generator.
  // Values are from https://www.qrcode.com/en/about/version.html
  const qrCodeCharacterLimitsForVersion = [
    20, 38, 61, 90, 122, 154, 178, 221, 262, 311, 366, 419, 483, 528, 600, 656, 734, 816, 909, 970, 1035, 1134, 1248,
    1326, 1451, 1542, 1637, 1732, 1839, 1994, 2113, 2238, 2369, 2506, 2632, 2780, 2894, 3054, 3220, 3391,
  ];
  let qrCodeVersion = qrCodeCharacterLimitsForVersion.length;
  for (let i = 0; i < qrCodeCharacterLimitsForVersion.length; i++) {
    if (url.length <= qrCodeCharacterLimitsForVersion[i]) {
      qrCodeVersion = i + 1;
      break;
    }
  }
  return qrCodeVersion;
}

export function generateQRCodeImage(data: string): string | undefined {
  try {
    const qrCode = qr(0, "M");
    qrCode.addData(data);
    qrCode.make();

    const moduleCount = qrCode.getModuleCount();
    const cellSize = 4;
    const size = moduleCount * cellSize;
    const rgba = new Uint8Array(size * size * 4);

    for (let y = 0; y < moduleCount; y++) {
      for (let x = 0; x < moduleCount; x++) {
        const color = qrCode.isDark(y, x) ? 0 : 255;
        for (let cy = 0; cy < cellSize; cy++) {
          for (let cx = 0; cx < cellSize; cx++) {
            const idx = ((y * cellSize + cy) * size + (x * cellSize + cx)) * 4;
            rgba[idx] = color;
            rgba[idx + 1] = color;
            rgba[idx + 2] = color;
            rgba[idx + 3] = 255;
          }
        }
      }
    }

    return encodePngDataUrl(size, size, rgba);
  } catch (err) {
    console.error("Error generating QR code:", err);
    return undefined;
  }
}

/**
 * Encodes RGBA pixel data as a PNG data URL without any native dependencies.
 * Uses stored (uncompressed) deflate blocks so no zlib library is needed.
 */
function encodePngDataUrl(width: number, height: number, rgba: Uint8Array): string {
  const signature = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);
  const ihdrChunk = pngChunk("IHDR", ihdrData(width, height));
  const idatChunk = pngChunk("IDAT", zlibStored(buildScanlines(width, height, rgba)));
  const iendChunk = pngChunk("IEND", new Uint8Array(0));

  const png = concat([signature, ihdrChunk, idatChunk, iendChunk]);
  let binary = "";
  for (let i = 0; i < png.length; i++) {
    binary += String.fromCharCode(png[i]);
  }
  return `data:image/png;base64,${btoa(binary)}`;
}

function ihdrData(width: number, height: number): Uint8Array {
  const buf = new Uint8Array(13);
  const view = new DataView(buf.buffer);
  view.setUint32(0, width);
  view.setUint32(4, height);
  buf[8] = 8; // bit depth
  buf[9] = 6; // color type RGBA
  return buf;
}

function buildScanlines(width: number, height: number, rgba: Uint8Array): Uint8Array {
  const rowBytes = 1 + width * 4; // filter byte + pixel data
  const raw = new Uint8Array(height * rowBytes);
  for (let y = 0; y < height; y++) {
    const offset = y * rowBytes;
    raw[offset] = 0; // filter: None
    raw.set(rgba.subarray(y * width * 4, (y + 1) * width * 4), offset + 1);
  }
  return raw;
}

/** Wraps raw data in a zlib container using stored (non-compressed) deflate blocks. */
function zlibStored(data: Uint8Array): Uint8Array {
  const maxBlock = 65535;
  const numBlocks = Math.ceil(data.length / maxBlock) || 1;
  const out = new Uint8Array(2 + data.length + numBlocks * 5 + 4);
  out[0] = 0x78;
  out[1] = 0x01; // CMF/FLG: deflate, no dict, check bits valid ((0x78 << 8 | 0x01) % 31 === 0)
  let pos = 2;
  for (let i = 0; i < numBlocks; i++) {
    const start = i * maxBlock;
    const len = Math.min(maxBlock, data.length - start);
    const isFinal = i === numBlocks - 1;
    out[pos++] = isFinal ? 0x01 : 0x00;
    out[pos++] = len & 0xff;
    out[pos++] = (len >> 8) & 0xff;
    out[pos++] = ~len & 0xff;
    out[pos++] = (~len >> 8) & 0xff;
    out.set(data.subarray(start, start + len), pos);
    pos += len;
  }
  const checksum = adler32(data);
  const view = new DataView(out.buffer);
  view.setUint32(pos, checksum);
  return out;
}

function pngChunk(type: string, data: Uint8Array): Uint8Array {
  const typeBytes = new Uint8Array([type.charCodeAt(0), type.charCodeAt(1), type.charCodeAt(2), type.charCodeAt(3)]);
  const chunk = new Uint8Array(4 + 4 + data.length + 4);
  const view = new DataView(chunk.buffer);
  view.setUint32(0, data.length);
  chunk.set(typeBytes, 4);
  chunk.set(data, 8);
  const crcInput = concat([typeBytes, data]);
  view.setUint32(8 + data.length, crc32(crcInput));
  return chunk;
}

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc ^= data[i];
    for (let j = 0; j < 8; j++) {
      crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function adler32(data: Uint8Array): number {
  let a = 1;
  let b = 0;
  for (let i = 0; i < data.length; i++) {
    a = (a + data[i]) % 65521;
    b = (b + a) % 65521;
  }
  return ((b << 16) | a) >>> 0;
}

function concat(arrays: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const a of arrays) {
    total += a.length;
  }
  const result = new Uint8Array(total);
  let offset = 0;
  for (const a of arrays) {
    result.set(a, offset);
    offset += a.length;
  }
  return result;
}
